import { Button, Card, Divider, Flex, Skeleton, Tag, Typography } from 'antd';
import Link from 'next/link';
import useSWR from 'swr';
import CancelModalForm from '../../utils/CancelModalForm';
import DownloadLimitWidget from '../../utils/donwload-limit-widget/DownloadLimitWidget';
import styles from './AppPlanCard.module.css';

const { Title, Text } = Typography;

const STATUS_COLOR = {
   active: 'success',
   past_due: 'warning',
   trialing: 'processing',
   canceled: 'default',
};

function formatDate(value) {
   if (!value) return null;
   return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
   });
}

export default function AppPlanCard(props) {
   const { user, subscriptions } = props;
   const isPaddle = subscriptions?.provider !== 'stripe' && Boolean(subscriptions?.paddleSubscriptionId);
   const isTrial = subscriptions?.status === 'trialing';

   const {
      data: paddleData,
      isLoading: paddleLoading,
   } = useSWR(isPaddle ? `/api/paddle/plan_details?subscription_id=${subscriptions.paddleSubscriptionId}` : null);
   const paddlePlan = paddleData?.paddleSubscription;
   const paddleCanceled = paddlePlan?.state === 'deleted';

   if (!user || !subscriptions) return null;

   // Status: prefer the Stripe webhook-managed status; for Paddle use the
   // upstream state because our DB row can lag.
   const statusLabel = isPaddle && paddlePlan?.state ? paddlePlan.state : subscriptions.status;
   const isCanceled = paddleCanceled || subscriptions.status === 'canceled';
   const cancelPending = !isCanceled && Boolean(subscriptions.cancelAtPeriodEnd);

   // Date the user keeps access until / is next charged.
   const accessEndsAt = isTrial
      ? subscriptions.trialEndsAt || subscriptions.nextBillDate
      : isPaddle && paddlePlan?.next_payment?.date
        ? paddlePlan.next_payment.date
        : subscriptions.nextBillDate;
   const lastPaymentDate = isPaddle ? paddlePlan?.last_payment?.date : null;

   const hasBillingSubscription = Boolean(
      subscriptions.paddleSubscriptionId || subscriptions.stripeSubscriptionId
   );
   const showCancelButton =
      hasBillingSubscription && !isCanceled && !cancelPending && Boolean(accessEndsAt);
   const showUpdatePayment = isPaddle && paddlePlan?.update_url && !paddleCanceled && !cancelPending;

   const nextPaymentLine = isCanceled
      ? `Your subscription has been canceled. You can still use your plan until ${formatDate(accessEndsAt) || '—'}`
      : cancelPending
        ? isTrial
           ? `Trial canceled — you won't be charged. Access continues until ${formatDate(accessEndsAt) || '—'}.`
           : `Subscription canceled — access continues until ${formatDate(accessEndsAt) || '—'}.`
        : isTrial
          ? `Trial ends ${formatDate(accessEndsAt) || '—'} — you'll be charged then unless you cancel.`
          : `Next payment: ${formatDate(accessEndsAt) || '—'}`;

   return (
      <Card variant="outlined" styles={{ body: { padding: 0 } }}>
         <Flex align="center" gap={12} style={{ padding: 12 }}>
            <Title level={5} className={styles.title} style={{ margin: 0 }}>
               Current Plan
            </Title>
            <Tag color={STATUS_COLOR[statusLabel] || 'default'}>{statusLabel}</Tag>
            {showUpdatePayment && (
               <Button size="small" href={paddlePlan.update_url} target="_blank">
                  Update Payment
               </Button>
            )}
         </Flex>
         <Divider style={{ margin: 0 }} />
         <Flex gap={12} justify="space-between" wrap="wrap" style={{ padding: 12 }}>
            <Flex vertical gap={16} style={{ flex: 1, minWidth: 300 }}>
               <Title level={3} className={styles.bigText}>
                  {subscriptions.subscriptionPlan?.name}
               </Title>
               {isPaddle && paddleLoading && <Skeleton.Input active size="small" style={{ width: 200 }} />}
               {lastPaymentDate && <p>Last payment: {formatDate(lastPaymentDate)}</p>}
               <Link href="/upgrade">
                  <Button type="primary" style={{ marginTop: 8, maxWidth: 200 }}>
                     Upgrade
                  </Button>
               </Link>
            </Flex>

            <Divider type="vertical" style={{ height: 'auto' }} />

            <Flex vertical gap={8} style={{ flex: 1, minWidth: 300 }}>
               <Flex gap={8} justify="space-between">
                  <Flex vertical gap={8}>
                     {isPaddle && paddleLoading ? (
                        <Skeleton.Input active size="small" style={{ width: 200 }} />
                     ) : (
                        <p>{nextPaymentLine}</p>
                     )}
                     {showCancelButton && <CancelModalForm subscription={subscriptions} />}
                  </Flex>
                  {!isCanceled && subscriptions.subscriptionPlan?.price != null && (
                     <Text style={{ fontSize: 32, color: 'var(--ant-color-primary, #1677ff)' }}>
                        {'$' + subscriptions.subscriptionPlan.price}
                     </Text>
                  )}
               </Flex>
               <Divider style={{ margin: 0 }} />
               <Flex gap={8}>
                  <DownloadLimitWidget />
               </Flex>
            </Flex>
         </Flex>
      </Card>
   );
}
